import React from 'react';
import { useNavigate } from 'react-router-dom';
import { bindPopover } from 'material-ui-popup-state';
import { Popover } from '@mui/material';

import { useGetAudiencesForMembershipWithPagination } from '@occc-app/dataAccessLayer/membership/queries';
import { routeCreator } from '@occc-app/routes/Audience/utils';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { AudienceFilterForm } from '@occc-app/routes/Audience/components/AudienceFilterForm';
import { useAudiencesFilterStatus } from '@occc-app/routes/Audience/AudienceListPage/useAudiencesFilterStatus';
import { AudienceFilterFormValues } from '@occc-app/routes/Audience/types';
import { Audience_Status } from '@occc/graphql';

import Audiences from './Audiences';
import { getAudienceFilter } from './utils';

import {
  AUDIENCES_AFTER_PARAM,
  AUDIENCES_BEFORE_PARAM,
  AUDIENCES_STATUS_PARAM,
} from '../../../constants';

export interface AudiencesContainerProps {
  memberId: string;
  popoverProps: ReturnType<typeof bindPopover>;
}

export const memberAudienceStatusOptions = [
  { label: 'Active', value: Audience_Status.Active },
  { label: 'Draft', value: Audience_Status.Draft },
  { label: 'Archived', value: Audience_Status.Archived },
];

export const AudiencesContainer: React.FC<AudiencesContainerProps> = ({
  memberId,
  popoverProps,
}) => {
  const navigate = useNavigate();

  const [status, setStatus] = useAudiencesFilterStatus(
    AUDIENCES_STATUS_PARAM,
    AUDIENCES_BEFORE_PARAM,
    AUDIENCES_AFTER_PARAM,
  );

  const [paginationInfo, onPageChange] = usePaginationParams({
    beforeParamName: AUDIENCES_BEFORE_PARAM,
    afterParamName: AUDIENCES_AFTER_PARAM,
  });

  const { loading, error, data, paginationProps } = useGetAudiencesForMembershipWithPagination({
    paginationInfo,
    onPageChange,
    filter: getAudienceFilter(memberId, status),
  });

  const onAudienceClick = (id: string) => {
    navigate(routeCreator.details(id));
  };

  const onSubmit = React.useCallback(
    (values: AudienceFilterFormValues) => {
      setStatus(values.status);
      popoverProps.onClose();
    },
    [popoverProps, setStatus],
  );

  const onReset = React.useCallback(() => {
    setStatus(Audience_Status.Active);
    popoverProps.onClose();
  }, [popoverProps, setStatus]);

  return (
    <>
      <Audiences
        audiences={data}
        error={error}
        loading={loading}
        paginationProps={paginationProps}
        onAudienceClick={onAudienceClick}
      />
      <Popover
        {...popoverProps}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        keepMounted
      >
        <AudienceFilterForm
          onSubmit={onSubmit}
          onReset={onReset}
          initialValues={{ status: status || Audience_Status.Active }}
          statusOptions={memberAudienceStatusOptions}
        />
      </Popover>
    </>
  );
};
